import * as d3 from 'd3-selection'
import { brushX } from 'd3-brush'

/*
 * d3-brush makes changing cursors a bit cumbersome. This defines a map of what cursors should be used
 * for the different components within our brush. When a `mousedown` event fires on an element that should
 * set a cursor, it will use a key/value from this map and that cursor will be applied to the overlay element
 * to override what d3-brush wants to do. See #73
 *
 * We can't just set these in css on the element with a `!important` because the cursor needs to be applied
 * to the `.overlay` when the user is dragging an element.
 */
const cursors = {
  overlay: 'pointer',
  selection: 'move',
  handle: 'ew-resize'
}

var cursorType = 'overlay' // This is the default

/*
 * Draws brush control over the minibar, allowing the user to see and control
 * what is in the main gantt viewport
 */
export default function (selection, dimensions, extent, xScale, animationDuration = 500) {
  selection.each(function (data) {
    if (isNaN(xScale.domain()[0].getTime())) {
      // If we don't have an extent, we can't use the brush
      return
    }
    var container = d3.select(this)
      , margin = dimensions.footer.margin
      , width = Math.max(1, dimensions.right.width - margin.left - margin.right) // 1 b/c you can't have a range of 0,0 w/ the brush
      , height = Math.max(0, dimensions.footer.height - margin.top - margin.bottom)
      , brush = brushX().extent([[0, 0], [width, height + margin.top + margin.bottom]])
                        .on('start', function () {
                          d3.select(this).select('.overlay')
                                         .attr('cursor', cursors[cursorType])
                        })
                        .on('brush', e => {
                          dimensions.footer.brush.width = e.selection[1] - e.selection[0]
                          extent[0] = xScale.invert(e.selection[0])
                          extent[1] = xScale.invert(e.selection[1])

                          // Update the location of our customer handlers
                          handle.attr('transform', function (_d, i) {
                                  return 'translate(' + e.selection[i] + ',' + 27 + ')'
                                })

                          container.dispatch('brush')
                        })
                        .on('end', function () {
                          d3.select(this).select('.overlay')
                                         .attr('cursor', cursors.overlay) // reset
                        })
      , group = container.selectAll('g.gantt-mini-brush')
                         .data(d => [d])
      , brushEnter = group.enter()
                          .append('g')
                            .attr('class', 'gantt-mini-brush')
                            .call(brush)

    brushEnter.selectAll('.handle').remove() // remove the default `rect` endpoint handlers created by d3

    brushEnter.selectAll('.selection')
              .on('mousedown', ()  => cursorType = 'selection')

    // Create custom handlers
    var _handle = brushEnter.selectAll('.brush-handle')
                            .data([{
                              type: 'w',
                              path: 'M.33333333 4.00533333c0 .196.07266667.392.21933334.54133334.00533333.006.012.00933333.01733333.01466666L3.716 7.7746667c.29133333.2973333.76333333.2973333 1.05466667 0 .29133333-.298.29133333-.7806667 0-1.078L2.138 4.008l2.64733333-2.70333333C5.078 1.006 5.078.522 4.78533333.224c-.292-.29866667-.766-.29866667-1.058 0L.558 3.46066667C.556 3.462.554 3.46333333.55133333 3.46533333c-.14533333.14933334-.218.34466667-.218.54', // the arrow shape
                              transform: 'translate(-3, -4)'
                            }, {
                              type: 'e',
                              path: 'M4.6715 4.00533333c0 .196-.07266667.392-.21933333.54133334-.00533334.006-.012.00933333-.01733334.01466666l-3.146 3.21333337C.9975 8.072.5255 8.072.2341667 7.7746667c-.2913334-.298-.2913334-.7806667 0-1.078L2.86683333 4.008.2195 1.30466667C-.0731667 1.006-.0731667.522.2195.224c.292-.29866667.766-.29866667 1.058 0l3.16933333 3.23666667c.002.00133333.004.00266666.00666667.00466666.14533333.14933334.218.34466667.218.54',
                              transform: 'translate(-2, -4)'
                            }])
      , handleEnter = _handle.enter()
                             .append('g')
                               .attr('class', 'brush-handle')
                               .on('mousedown', () => cursorType = 'handle')
      , handle = container.selectAll('g.gantt-mini-brush .brush-handle')
                          .merge(handleEnter)

    handleEnter.append('circle')
                 .attr('r', 8)

    handleEnter.append('g')
               .attr('transform', d => d.transform)
               .append('path')
               .attr('class', 'brush-handle-icon')
               .attr('d', d => d.path)

    brushEnter.call(brush.move, extent.map(xScale)) // Initialize the brush to full extent with no transitions
              .selectAll('.overlay')
              .on('mousedown touchstart', function (e, d) {
                e.stopPropagation()
                var cx = d3.pointer(e)[0]
                  , x0 = cx - dimensions.footer.brush.width / 2
                  , x1 = cx + dimensions.footer.brush.width / 2
                  , d = x1 > width ? [width - dimensions.footer.brush.width, width] : x0 < 0 ? [0, dimensions.footer.brush.width] : [x0, x1]

                d3.select(this.parentNode)
                  .transition()
                  .duration(animationDuration)
                  .call(brush.move, d)
              })

    group.call(brush)
         .transition()
         .duration(animationDuration)
         .call(brush.move, extent.map(xScale))
  })
}
